import { FacetName, FilterName } from '../../__generated__/fed-graphql-types'
import { CanonicalSearchFinanceOption, CanonicalSearchRangeValues } from '../../patterns/saved-searches/save-search'
import { CAPITALISED_MAKE_EXCEPTIONS, capitaliseWord, formatRange, formatNumber } from './saved-search-refinement.util'

interface CanonicalSearchValuesParser {
    [key: string]: {
        getFormattedValue(searchValue?: string[] | CanonicalSearchRangeValues | CanonicalSearchFinanceOption): string
        facetName?: string | SearchFacetRangeNames | SearchFacetFinanceOptionNames
    }
}

interface SearchFacetRangeNames {
    from?: string
    to?: string
}

interface SearchFacetFinanceOptionNames {
    financeDeposit: string
    financeTerm: string
    financeYearlyMileage: string
}

export const canonicalSearchValuesParser: CanonicalSearchValuesParser = {
    RADIUS: {
        getFormattedValue(searchValue: string[]): string {
            if (Number(searchValue[0]) >= 1500) {
                return 'National'
            }
            return searchValue[0] + ' miles'
        },
        facetName: FacetName.distance,
    },
    POSTCODE: {
        getFormattedValue(searchValue: string[]): string {
            return searchValue[0].replace(/^(.*)(\d)/, '$1 $2').toUpperCase()
        },
        facetName: FacetName.postcode,
    },
    CATEGORY_TAG: {
        getFormattedValue(searchValue: string[]): string {
            switch (searchValue[0]) {
                case 'omg':
                    return 'OMG'
                case 'bigboot':
                    return 'Big Boots'
                case 'cheap_to_insure':
                    return 'Cheaper insurance'
                case 'classic':
                    return 'Classic'
                case 'firstcar':
                    return 'First Cars'
                case 'economical':
                    return 'Lower running costs'
                case 'luxury':
                    return 'Luxury'
                case 'smallcar':
                    return 'Small cars'
                case 'smallsuv':
                    return 'Small SUVs'
                case 'sport':
                    return 'Sporty'
                case 'big_family':
                    return 'Cars for big families'

                default:
                    return searchValue[0]
            }
        },
        facetName: FacetName.category_tag!,
    },
    MAKE: {
        getFormattedValue(searchValue: string[]): string {
            if (CAPITALISED_MAKE_EXCEPTIONS.includes(searchValue[0])) {
                return searchValue[0]
            }
            return capitaliseWord(searchValue[0])
        },
        facetName: FacetName.make!,
    },
    MODEL: {
        getFormattedValue(searchValue: string[]): string {
            return capitaliseWord(searchValue[0])
        },
        facetName: FacetName.model!,
    },
    TRIM: {
        getFormattedValue(searchValue: string[]): string {
            return searchValue[0]
        },
        facetName: FacetName.aggregated_trim!,
    },
    STYLE: {
        getFormattedValue(searchValue: string[]): string {
            return capitaliseWord(searchValue[0])
        },
        facetName: FacetName.style,
    },
    SEATS_VALUES: {
        getFormattedValue(searchValue: string[]): string {
            return `${searchValue} seats`
        },
        facetName: FacetName.seats_values,
    },
    BADGE_ENGINE_SIZE: {
        getFormattedValue(searchValue: { from: string; to: string }): string {
            return formatRange(formatNumber(searchValue.from), formatNumber(searchValue.to), '', 'L')
        },
        facetName: {
            from: FilterName.min_engine_size,
            to: FilterName.max_engine_size,
        },
    },
    MINIMUM_BADGE_ENGINE_SIZE: {
        getFormattedValue(searchValue: string[]): string {
            return `From ${searchValue[0]}L`
        },
        facetName: {
            from: FilterName.min_engine_size,
        },
    },
    MAXIMUM_BADGE_ENGINE_SIZE: {
        getFormattedValue(searchValue: string[]): string {
            return `Up to ${searchValue[0]}L`
        },
        facetName: {
            to: FilterName.max_engine_size,
        },
    },
    CC: {
        getFormattedValue(searchValue: { from: string; to: string }): string {
            return formatRange(formatNumber(searchValue.from), formatNumber(searchValue.to), '', 'cc')
        },
        facetName: {
            from: FilterName.min_engine_size_cc,
            to: FilterName.max_engine_size_cc,
        },
    },
    CC_FROM: {
        getFormattedValue(searchValue: string[]): string {
            return `From ${searchValue[0]}cc`
        },
        facetName: {
            from: FilterName.min_engine_size_cc,
        },
    },
    CC_TO: {
        getFormattedValue(searchValue: string[]): string {
            return `Up to ${searchValue[0]}cc`
        },
        facetName: {
            to: FilterName.max_engine_size_cc,
        },
    },
    INSURANCEGROUP: {
        getFormattedValue(searchValue: string[]): string {
            return `Insurance Group: Up to ${searchValue[0].slice(0, -1)}`
        },
        facetName: FacetName.insurance_group,
    },
    BERTH: {
        getFormattedValue(searchValue: string[]): string {
            return `${searchValue[0]} berth`
        },
        facetName: FacetName.berth,
    },
    PRICE: {
        getFormattedValue(searchValue: { from: string; to: string }): string {
            return formatRange(formatNumber(searchValue.from), formatNumber(searchValue.to), '£', '')
        },
        facetName: {
            from: FilterName.min_price,
            to: FilterName.max_price,
        },
    },
    PRICE_FROM: {
        getFormattedValue(searchValue: string[]): string {
            return `From £${formatNumber(searchValue[0])}`
        },
        facetName: {
            from: FilterName.min_price,
        },
    },
    PRICE_TO: {
        getFormattedValue(searchValue: string[]): string {
            return `Up to £${formatNumber(searchValue[0])}`
        },
        facetName: {
            to: FilterName.max_price,
        },
    },
    MONTHLY_PRICE: {
        getFormattedValue(searchValue: { from: string; to: string }): string {
            return formatRange(formatNumber(searchValue.from), formatNumber(searchValue.to), '£', '')
        },
        facetName: {
            from: FilterName.min_monthly_price,
            to: FilterName.max_monthly_price,
        },
    },
    MINIMUM_MONTHLY_PRICE: {
        getFormattedValue(searchValue: string[]): string {
            return `From £${formatNumber(searchValue[0])}`
        },
        facetName: {
            from: FilterName.min_monthly_price,
        },
    },
    MAXIMUM_MONTHLY_PRICE: {
        getFormattedValue(searchValue: string[]): string {
            return `Up to £${formatNumber(searchValue[0])}`
        },
        facetName: {
            to: FilterName.max_monthly_price,
        },
    },
    FINANCE_OPTIONS: {
        getFormattedValue(searchValue: {
            financeDeposit: string
            financeTerm: string
            financeYearlyMileage: string
        }): string {
            return `£${searchValue.financeDeposit} deposit, ${searchValue.financeTerm} months, ${formatNumber(
                searchValue.financeYearlyMileage
            )} miles`
        },
        facetName: {
            financeDeposit: FilterName.deposit,
            financeTerm: FilterName.term,
            financeYearlyMileage: FilterName.yearly_mileage,
        },
    },
    YEAR: {
        getFormattedValue(searchValue: { from: string; to: string }): string {
            return formatRange(searchValue.from, searchValue.to, '', '')
        },
        facetName: {
            from: FilterName.min_year_manufactured,
            to: FilterName.max_year_manufactured,
        },
    },
    YEAR_FROM: {
        getFormattedValue(searchValue: string[]): string {
            return `From ${searchValue[0]}`
        },
        facetName: {
            from: FilterName.min_year_manufactured,
        },
    },
    YEAR_TO: {
        getFormattedValue(searchValue: string[]): string {
            return `Up to ${searchValue[0]}`
        },
        facetName: {
            to: FilterName.max_year_manufactured,
        },
    },
    FUEL_TYPE: {
        getFormattedValue(searchValues: string[]): string {
            return searchValues.join(', ')
        },
        facetName: FacetName.fuel_type,
    },
    DRIVETRAIN: {
        getFormattedValue(searchValues: string[]): string {
            return searchValues[0]
        },
        facetName: FacetName.drivetrain,
    },
    BATTERY_RANGE: {
        getFormattedValue(searchValue: string[]): string {
            return `Battery Range: Up to ${searchValue[0].match(/\d+/)?.join()} miles`
        },
        facetName: FacetName.battery_range_values,
    },
    BATTERY_CHARGE_TIME: {
        getFormattedValue(searchValue: string[]): string {
            const prefix = searchValue[0].split('_')[0] === 'OVER' ? 'Over' : 'Up to'
            return `Battery Charge Time: ${prefix} ${searchValue[0].match(/\d+/)?.join()} hours`
        },
        facetName: FacetName.battery_charge_time_values,
    },
    BATTERY_QUICK_CHARGE_TIME: {
        getFormattedValue(searchValue: string[]): string {
            return `Battery Quick Charge Time: Up to ${searchValue[0].match(/\d+/)?.join()} hours`
        },
        facetName: FacetName.battery_quick_charge_time_values,
    },
    FUEL_CONSUMPTION: {
        getFormattedValue(searchValue: string[]): string {
            return `Fuel Consumption: ${searchValue[0].match(/\d+/)?.join()}+ mpg`
        },
        facetName: FacetName.fuel_consumption_values,
    },
    MILEAGE: {
        getFormattedValue(searchValue: { from: string; to: string }): string {
            return formatRange(formatNumber(searchValue.from), formatNumber(searchValue.to), '', ' miles')
        },
        facetName: {
            from: FilterName.min_mileage,
            to: FilterName.max_mileage,
        },
    },
    MINIMUM_MILEAGE: {
        getFormattedValue(searchValue: string[]): string {
            return `From ${formatNumber(searchValue[0])} miles`
        },
        facetName: {
            from: FilterName.min_mileage,
        },
    },
    MAXIMUM_MILEAGE: {
        getFormattedValue(searchValue: string[]): string {
            return `Up to ${formatNumber(searchValue[0])} miles`
        },
        facetName: {
            to: FilterName.max_mileage,
        },
    },
    WHEELBASE: {
        getFormattedValue(searchValue: string[]): string {
            return searchValue[0]
        },
        facetName: FacetName.wheelbase,
    },
    BODY_TYPE: {
        getFormattedValue(searchValues: string[]): string {
            return searchValues.join(', ')
        },
        facetName: FacetName.body_type,
    },
    END_LAYOUT: {
        getFormattedValue(searchValue: string[]): string {
            return searchValue[0]
        },
        facetName: FacetName.end_layout,
    },
    BEDROOM_LAYOUT: {
        getFormattedValue(searchValue: string[]): string {
            return searchValue[0]
        },
        facetName: FacetName.bedroom_layout,
    },
    DRIVER_POSITION: {
        getFormattedValue(searchValue: string[]): string {
            return searchValue[0]
        },
        facetName: FacetName.driver_position,
    },
    MTPLM: {
        getFormattedValue(searchValue: { from: string; to: string }): string {
            return formatRange(searchValue.from, searchValue.to, '', 'kg')
        },
        facetName: {
            from: FilterName.min_mtplm,
            to: FilterName.max_mtplm,
        },
    },
    MINIMUM_MTPLM: {
        getFormattedValue(searchValue: string[]): string {
            return `From ${searchValue[0]}kg`
        },
        facetName: {
            from: FilterName.min_mtplm,
        },
    },
    MAXIMUM_MTPLM: {
        getFormattedValue(searchValue: string[]): string {
            return `Up to ${searchValue[0]}kg`
        },
        facetName: {
            to: FilterName.max_mtplm,
        },
    },
    LENGTH: {
        getFormattedValue(searchValue: { from: string; to: string }): string {
            return formatRange(searchValue.from, searchValue.to, '', 'mm')
        },
        facetName: {
            from: FilterName.min_exterior_length,
            to: FilterName.max_exterior_length,
        },
    },
    MINIMUM_LENGTH: {
        getFormattedValue(searchValue: string[]): string {
            return `From ${searchValue[0]}mm`
        },
        facetName: {
            from: FilterName.min_exterior_length,
        },
    },
    MAXIMUM_LENGTH: {
        getFormattedValue(searchValue: string[]): string {
            return `Up to ${searchValue[0]}mm`
        },
        facetName: {
            to: FilterName.max_exterior_length,
        },
    },
    TRANSMISSION: {
        getFormattedValue(searchValue: string[]): string {
            return searchValue[0]
        },
        facetName: FacetName.transmission,
    },
    DOORS: {
        getFormattedValue(searchValue: string[]): string {
            return `${searchValue[0]} doors`
        },
        facetName: FacetName.doors_values,
    },
    COLOUR: {
        getFormattedValue(searchValues: string[]): string {
            return searchValues.map((value) => capitaliseWord(value)).join(', ')
        },
        facetName: FacetName.colour,
    },
    SELLER_TYPE: {
        getFormattedValue(searchValue: string[]): string {
            return `${capitaliseWord(searchValue[0])} sale`
        },
        facetName: FacetName.seller_type,
    },
    IS_SELLER_DEFINED_USED: {
        getFormattedValue(searchValue: string[]): string {
            return searchValue[0] === 'true' ? 'Used' : 'New'
        },
        facetName: FilterName.is_seller_defined_used,
    },
    CAB_TYPE: {
        getFormattedValue(searchValues: string[]): string {
            return searchValues[0]
        },
        facetName: FacetName.cab_type,
    },
    ANNUAL_TAX_CARS: {
        getFormattedValue(searchValue: string[]): string {
            const searchValueArr = searchValue[0].split('_')
            const prefix =
                searchValueArr[0] === 'OVER'
                    ? 'Annual tax over '
                    : searchValueArr[0] === 'TO'
                    ? 'Annual tax up to '
                    : ''
            const displayValue = searchValueArr[1] === '0' ? 'No annual tax' : `£${searchValueArr[1]}`
            return `${prefix}${displayValue}`
        },
        facetName: FacetName.annual_tax_values,
    },
    CO2_EMISSIONS_CARS: {
        getFormattedValue(searchValue: string[]): string {
            const searchValueArr = searchValue[0].split('_')
            const prefix = searchValueArr[0] === 'TO' && searchValueArr[1] !== '0' ? 'Up to ' : ''
            const suffix = searchValueArr[0] === 'OVER' ? ' and above' : ''
            return `${prefix}${searchValueArr[1]} g / km CO2${suffix}`
        },
        facetName: FacetName.co2_emission_values,
    },
    ZERO_TO_60: {
        getFormattedValue(searchValue: string[]): string {
            return `0-60 in ${searchValue[0].match(/\d+/)?.join()}s`
        },
        facetName: FacetName.acceleration_values,
    },
    KEYWORDS: {
        getFormattedValue(searchValue: string[]): string {
            return searchValue.join(', ')
        },
        facetName: FacetName.keywords,
    },
    WITH_MANUFACTURER_RRP_SAVING: {
        getFormattedValue(): string {
            return 'Only show new car deals'
        },
        facetName: FacetName.with_manufacturer_rrp_saving,
    },
    IS_WRITEOFF: {
        getFormattedValue(searchValue: string[]): string {
            const isWriteOFf = searchValue[0] === 'true'
            return isWriteOFf ? 'CAT S/C/D/N only' : 'CAT S/C/D/N excluded'
        },
        facetName: FacetName.is_writeoff,
    },
    ULEZ_COMPLIANT: {
        getFormattedValue(): string {
            return 'ULEZ Compliant'
        },
        facetName: FacetName.ulez_compliant,
    },
    NI_ONLY: {
        getFormattedValue(): string {
            return 'N.Ireland adverts only'
        },
        facetName: FacetName.ni_only,
    },
}

export const allCanonicalSearchKeys: string[] = Object.keys(canonicalSearchValuesParser)
